/**
 * Session stimulus bus — generic trailing-edge coalescing turn driver.
 *
 * Connectors that observe state changes (FlowAdapter, future alert/inbox/
 * file-watch connectors) call `bus.signal(connectorId, { promptFragment, meta })`
 * to request that the agent be woken with a fresh turn. Multiple signals from
 * the same connector arriving within `coalesceMs` are folded into one turn,
 * with their `promptFragment`s concatenated by `\n\n` and their `meta` objects
 * collected into an array.
 *
 * Per-`connectorId` queues — concurrent connectors NEVER coalesce together;
 * each runs its own pending timer and turn pipeline.
 *
 * If `signal()` is called for a connector whose driveTurn is already in flight,
 * the new signal queues for the next cycle (a fresh timer starts only after
 * the in-flight turn settles).
 *
 * Distinct from `refresh-flag-dispatcher.ts`'s leading-edge debounce: that one
 * fires immediately and drops within a 2 s window. The stimulus bus is trailing-
 * edge collapse with a returned promise, so callers can `await` completion of
 * the turn that drained their signal.
 *
 * @docLink runner#session-stimulus-bus
 * @since 2.2.0
 */

import type { Logger } from "@skaile/workspaces/types";

/**
 * One stimulus from a connector. The bus collapses contiguous signals from the
 * same connector into a single turn whose prompt is built by joining the
 * `promptFragment`s with `\n\n`.
 */
export interface StimulusSignal {
  /** Fragment the connector wants prepended to the next turn prompt. */
  promptFragment: string;
  /** Optional event metadata for tracing. Connector-defined. */
  meta?: Record<string, unknown>;
  /**
   * Trailing-edge coalesce window — multiple signals from the same connector
   * arriving within this many ms are folded into one turn with their
   * fragments concatenated. Default: 50 ms (a tick to let near-simultaneous
   * signals settle without perceptible delay).
   */
  coalesceMs?: number;
}

/**
 * Public surface returned by `createSessionStimulusBus`. Connectors hold a
 * reference to this and call `signal(...)` whenever they want to wake the
 * agent.
 */
export interface SessionStimulusBus {
  /**
   * Request a turn kick for `connectorId`. Resolves once the resulting turn
   * has drained (the supplied `driveTurn` resolved). Multiple parallel calls
   * within `coalesceMs` collapse into one turn and share a single resolution.
   */
  signal(connectorId: string, sig: StimulusSignal): Promise<void>;
}

/**
 * Drives one agent turn given a concatenated prompt fragment. The runner
 * supplies this when constructing the bus — typically wraps `driver.prompt`
 * + waits for `agent_end`. The bus calls it with all coalesced fragments
 * joined by newline.
 */
export type TurnDriver = (
  promptFragment: string,
  batchedMeta: Array<Record<string, unknown>>,
) => Promise<void>;

export interface CreateStimulusBusOptions {
  driveTurn: TurnDriver;
  /** Default coalesce window when a signal omits its own. */
  defaultCoalesceMs?: number;
  log?: Logger;
}

/** Default coalesce window — kept consistent with the spec's stated default. */
export const DEFAULT_COALESCE_MS = 50;

interface PendingEntry {
  signal: StimulusSignal;
  resolve: () => void;
  reject: (err: unknown) => void;
}

interface ConnectorQueue {
  /** Buffered entries waiting for the current timer to fire. */
  pending: PendingEntry[];
  /** The timer that will fire `flushQueue`. `null` when no timer is active. */
  timer: ReturnType<typeof setTimeout> | null;
  /**
   * `true` when `driveTurn` is currently running for this connector. New
   * signals arriving while this is set queue up but do NOT start their timer
   * until the in-flight turn settles.
   */
  inFlight: boolean;
}

/**
 * Construct a fresh stimulus bus. The caller (typically `serve.ts` or
 * `run-flow.ts`) provides `driveTurn`, which kicks one agent turn given a
 * joined prompt + batched meta, and resolves once the turn drains.
 */
export function createSessionStimulusBus(opts: CreateStimulusBusOptions): SessionStimulusBus {
  const { driveTurn, defaultCoalesceMs = DEFAULT_COALESCE_MS, log } = opts;
  const queues = new Map<string, ConnectorQueue>();

  function getQueue(connectorId: string): ConnectorQueue {
    let q = queues.get(connectorId);
    if (!q) {
      q = { pending: [], timer: null, inFlight: false };
      queues.set(connectorId, q);
    }
    return q;
  }

  function startTimerIfNeeded(connectorId: string, q: ConnectorQueue, coalesceMs: number) {
    if (q.timer != null) return;
    if (q.inFlight) return; // wait until the in-flight turn settles
    q.timer = setTimeout(() => {
      q.timer = null;
      void flushQueue(connectorId, q);
    }, coalesceMs);
  }

  async function flushQueue(connectorId: string, q: ConnectorQueue): Promise<void> {
    if (q.pending.length === 0) return;
    const batch = q.pending;
    q.pending = [];
    q.inFlight = true;

    const fragments = batch.map((e) => e.signal.promptFragment);
    const metas = batch
      .map((e) => e.signal.meta)
      .filter((m): m is Record<string, unknown> => m !== undefined);
    const joined = fragments.join("\n\n");

    log?.debug("turn driving", {
      connectorId,
      count: batch.length,
      fragmentLength: joined.length,
    });

    try {
      await driveTurn(joined, metas);
      log?.debug("turn drained", {
        connectorId,
        count: batch.length,
        fragmentLength: joined.length,
      });
      for (const entry of batch) entry.resolve();
    } catch (err) {
      log?.debug("turn failed", {
        connectorId,
        count: batch.length,
        fragmentLength: joined.length,
        error: err instanceof Error ? err.message : String(err),
      });
      for (const entry of batch) entry.reject(err);
    } finally {
      q.inFlight = false;
      // If signals arrived while the turn was in flight, kick a fresh timer
      // for the next cycle so the next batch drains.
      if (q.pending.length > 0) {
        // Use the most recent signal's coalesceMs as the next window — it's
        // the simplest predictable choice when callers may have heterogeneous
        // windows.
        const nextWindow = q.pending[q.pending.length - 1]?.signal.coalesceMs ?? defaultCoalesceMs;
        startTimerIfNeeded(connectorId, q, nextWindow);
      }
    }
  }

  return {
    signal(connectorId: string, sig: StimulusSignal): Promise<void> {
      const coalesceMs = sig.coalesceMs ?? defaultCoalesceMs;
      log?.debug("stimulus signaled", {
        connectorId,
        fragmentLength: sig.promptFragment.length,
      });
      return new Promise<void>((resolve, reject) => {
        const q = getQueue(connectorId);
        q.pending.push({ signal: sig, resolve, reject });
        startTimerIfNeeded(connectorId, q, coalesceMs);
      });
    },
  };
}
